import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  CircularProgress,
  Alert,
  Paper,
  Tooltip,
  Badge,
  Divider,
  Grid,
} from '@mui/material';
import {
  PersonAdd,
  Phone,
  Event,
  Description,
  CheckCircle,
  Star,
  MoreVert,
  Email,
  Visibility,
  Edit,
  AttachMoney,
  TrendingUp,
  People,
  Refresh,
  Add,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import { getAuthToken } from '../../lib/authToken';

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  clientType: string;
  referralSource: string;
  trustBalance: number;
  pipelineStage: string;
  pipelineStageUpdatedAt: string;
  createdAt: string;
  notes: string;
}

interface PipelineStage {
  id: string;
  name: string;
  color: string;
  icon: string;
  clients: Client[];
  count: number;
}

// Fetch pipeline data
const fetchPipelineData = async () => {
  const token = getAuthToken();
  const response = await fetch('/api/clients/pipeline/kanban', {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch pipeline data');
  return response.json();
};

// Update client pipeline stage
const updatePipelineStage = async ({ clientId, pipelineStage }: { clientId: string; pipelineStage: string }) => {
  const token = getAuthToken();
  const response = await fetch(`/api/clients/${clientId}/pipeline-stage`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ pipelineStage }),
  });
  if (!response.ok) throw new Error('Failed to update pipeline stage');
  return response.json();
};

// Draggable Client Card Component
const DraggableClientCard: React.FC<{
  client: Client;
  onView: () => void;
  onEdit: () => void;
}> = ({ client, onView, onEdit }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: client.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getReferralIcon = (source: string) => {
    switch (source) {
      case 'website': return '🌐';
      case 'referral': return '👥';
      case 'google_ads': return '📢';
      case 'social_media': return '📱';
      default: return '📋';
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        mb: 1.5,
        cursor: 'grab',
        '&:hover': {
          boxShadow: 3,
        },
        '&:active': {
          cursor: 'grabbing',
        },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.75rem',
                bgcolor: theme.palette.primary.main,
                mr: 1,
              }}
            >
              {getInitials(client.firstName, client.lastName)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {client.firstName} {client.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {client.email || 'No email'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {client.referralSource && (
            <Tooltip title={`Source: ${client.referralSource.replace('_', ' ')}`}>
              <Chip
                label={getReferralIcon(client.referralSource)}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Tooltip>
          )}
          {client.clientType && (
            <Chip
              label={client.clientType}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          )}
          {client.trustBalance > 0 && (
            <Chip
              icon={<AttachMoney sx={{ fontSize: '0.7rem !important' }} />}
              label={`$${client.trustBalance.toLocaleString()} (demo)`}
              size="small"
              color="success"
              title="Demo only — not IOLTA-compliant"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          )}
        </Box>

        {client.phone && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            📞 {client.phone}
          </Typography>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => { setAnchorEl(null); onView(); }}>
            <Visibility fontSize="small" sx={{ mr: 1 }} /> View Details
          </MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); onEdit(); }}>
            <Edit fontSize="small" sx={{ mr: 1 }} /> Edit Client
          </MenuItem>
          <MenuItem
            disabled={!client.email}
            onClick={() => {
              setAnchorEl(null);
              if (client.email) window.location.href = `mailto:${client.email}`;
            }}
          >
            <Email fontSize="small" sx={{ mr: 1 }} /> Send Email
          </MenuItem>
          <MenuItem
            disabled={!client.phone}
            onClick={() => {
              setAnchorEl(null);
              if (client.phone) window.location.href = `tel:${client.phone}`;
            }}
          >
            <Phone fontSize="small" sx={{ mr: 1 }} /> Call
          </MenuItem>
        </Menu>
      </CardContent>
    </Card>
  );
};

// Pipeline Column Component — column itself is a droppable so empty stages accept cards
const PipelineColumn: React.FC<{
  stage: PipelineStage;
  onClientView: (client: Client) => void;
  onClientEdit: (client: Client) => void;
}> = ({ stage, onClientView, onClientEdit }) => {
  const theme = useTheme();
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });

  const getStageIcon = (iconName: string) => {
    switch (iconName) {
      case 'PersonAdd': return <PersonAdd />;
      case 'Phone': return <Phone />;
      case 'Event': return <Event />;
      case 'Description': return <Description />;
      case 'CheckCircle': return <CheckCircle />;
      case 'Star': return <Star />;
      default: return <People />;
    }
  };

  return (
    <Paper
      sx={{
        flex: 1,
        minWidth: 280,
        maxWidth: 350,
        bgcolor: 'background.default',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 280px)',
        minHeight: 400,
        outline: isOver ? `2px solid ${stage.color}` : 'none',
        outlineOffset: -2,
      }}
    >
      {/* Column Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: stage.color,
          color: 'white',
          borderRadius: '8px 8px 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getStageIcon(stage.icon)}
          <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600 }}>
            {stage.name}
          </Typography>
        </Box>
        <Badge badgeContent={stage.count} color="default" sx={{ '& .MuiBadge-badge': { bgcolor: 'white', color: stage.color } }}>
          <Box />
        </Badge>
      </Box>

      {/* Column Content — droppable target for this stage */}
      <Box
        ref={setNodeRef}
        sx={{
          flex: 1,
          p: 1,
          overflowY: 'auto',
          bgcolor: isOver
            ? (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100')
            : (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
          transition: 'background-color 0.15s ease',
        }}
      >
        <SortableContext items={stage.clients.map(c => c.id)} strategy={verticalListSortingStrategy}>
          {stage.clients.length > 0 ? (
            stage.clients.map((client) => (
              <DraggableClientCard
                key={client.id}
                client={client}
                onView={() => onClientView(client)}
                onEdit={() => onClientEdit(client)}
              />
            ))
          ) : (
            <Box
              sx={{
                height: '100%',
                minHeight: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                fontStyle: 'italic',
                border: isOver ? `1px dashed ${stage.color}` : '1px dashed transparent',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">Drop clients here</Typography>
            </Box>
          )}
        </SortableContext>
      </Box>
    </Paper>
  );
};

// Main Intake Pipeline Page
const IntakePipelinePage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch pipeline data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['intake-pipeline', user?.tenantId],
    queryFn: fetchPipelineData,
    refetchInterval: 30000,
  });

  // Update pipeline stage mutation
  const updateStageMutation = useMutation({
    mutationFn: updatePipelineStage,
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['intake-pipeline', user?.tenantId] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const stages: PipelineStage[] = data?.data?.stages || [];
  const stats = data?.data?.stats || {};

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeClientId = String(active.id);
    const overId = String(over.id);

    const sourceStage = stages.find((s) => s.clients.some((c) => c.id === activeClientId));
    if (!sourceStage) return;

    // Drop target is either a column (stage id) or another client card
    const stageIds = new Set(stages.map((s) => s.id));
    let destStageId: string | undefined;
    if (stageIds.has(overId)) {
      destStageId = overId;
    } else {
      destStageId = stages.find((s) => s.clients.some((c) => c.id === overId))?.id;
    }

    if (destStageId && sourceStage.id !== destStageId) {
      updateStageMutation.mutate({
        clientId: activeClientId,
        pipelineStage: destStageId,
      });
    }
  };

  const activeClient = activeId
    ? stages.flatMap((s) => s.clients).find((c) => c.id === activeId) || null
    : null;

  const handleClientView = (client: Client) => {
    setSelectedClient(client);
    setViewDialogOpen(true);
  };

  const handleClientEdit = (client: Client) => {
    navigate(`/clients?edit=${client.id}`);
  };

  return (
    <Box>
      {error && (
        <ListLoadErrorAlert
          resourceLabel="pipeline clients"
          onRetry={() => refetch()}
        />
      )}
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Client Intake Pipeline
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Drag and drop clients to move them through the intake process
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/clients?create=true')}
            >
              New Lead
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">Total Leads</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats.totalLeads || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">New Leads</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                  {stats.newLeads || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">In Progress</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {stats.inProgress || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">Conversion Rate</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {stats.conversionRate || 0}%
                  </Typography>
                  <TrendingUp sx={{ ml: 1, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Kanban Board */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 2,
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'background.default',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'grey.400',
                borderRadius: 4,
              },
            }}
          >
            {stages.map((stage) => (
              <PipelineColumn
                key={stage.id}
                stage={stage}
                onClientView={handleClientView}
                onClientEdit={handleClientEdit}
              />
            ))}
          </Box>
          <DragOverlay>
            {activeClient ? (
              <Card sx={{ width: 280, boxShadow: 6, cursor: 'grabbing', opacity: 0.95 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {activeClient.firstName} {activeClient.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activeClient.email || 'No email'}
                  </Typography>
                </CardContent>
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Client View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Client Details
        </DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ width: 60, height: 60, mr: 2, bgcolor: 'primary.main' }}>
                  {selectedClient.firstName?.charAt(0)}{selectedClient.lastName?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {selectedClient.firstName} {selectedClient.lastName}
                  </Typography>
                  <Chip
                    label={selectedClient.pipelineStage?.replace('_', ' ')}
                    size="small"
                    color="primary"
                  />
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedClient.email || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedClient.phone || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Client Type</Typography>
                  <Typography variant="body1">{selectedClient.clientType || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Referral Source</Typography>
                  <Typography variant="body1">{selectedClient.referralSource?.replace('_', ' ') || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Trust Balance</Typography>
                  <Typography variant="body1" color="success.main" fontWeight="bold">
                    ${(selectedClient.trustBalance || 0).toLocaleString()}
                  </Typography>
                  <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                    Demo only — not IOLTA-compliant. Do not use for real client trust funds.
                  </Alert>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Created</Typography>
                  <Typography variant="body1">
                    {selectedClient.createdAt ? format(new Date(selectedClient.createdAt), 'MMM d, yyyy') : 'N/A'}
                  </Typography>
                </Grid>
                {selectedClient.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1">{selectedClient.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setViewDialogOpen(false);
              if (selectedClient) handleClientEdit(selectedClient);
            }}
          >
            Edit Client
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntakePipelinePage;
